import React, { Children, useEffect, useMemo, useRef } from 'react';
import Slider from 'react-slick';

/**
 * CommonSlider
 *
 * A reusable carousel/slider for dashboard cards. It groups children into
 * pages (slides) of `itemsPerSlide` and lays them out as a CSS grid with
 * `columns` per row inside each slide.
 *
 * Reuses the `.advisor-carousel-*` CSS classes already defined in the
 * project stylesheet so that navigation arrows, dots and layout stay
 * consistent everywhere.
 *
 * Props:
 *  - children:       React nodes to be rendered inside the slider.
 *  - itemsPerSlide:  Number of items shown on a single slide (default 6).
 *  - columns:        Grid columns per slide (default 3).
 *  - className:      Extra class for the outer wrapper.
 *  - slideClassName: Extra class for each slide container.
 *  - gridStyle:      Inline style overrides for the inner grid.
 */
const CommonSlider = ({
	children,
	itemsPerSlide = 6,
	columns = 3,
	className = '',
	slideClassName = '',
	gridStyle = {},
}) => {
	const sliderRef = useRef(null);
	const wrapperRef = useRef(null);

	const items = useMemo(
		() => Children.toArray(children).filter(Boolean),
		[children]
	);

	const groupedItems = useMemo(() => {
		const groups = [];
		for (let i = 0; i < items.length; i += itemsPerSlide) {
			groups.push(items.slice(i, i + itemsPerSlide));
		}
		return groups;
	}, [items, itemsPerSlide]);

	useEffect(() => {
		if (!sliderRef.current || !wrapperRef.current) return;
		const recalculate = () => {
			if (sliderRef.current) sliderRef.current.slickGoTo(0, true);
		};
		const t1 = setTimeout(recalculate, 50);
		const t2 = setTimeout(recalculate, 200);
		window.addEventListener('resize', recalculate);
		return () => {
			clearTimeout(t1);
			clearTimeout(t2);
			window.removeEventListener('resize', recalculate);
		};
	}, [groupedItems.length]);

	if (items.length === 0) return null;

	const showControls = groupedItems.length > 1;

	return (
		<div
			ref={wrapperRef}
			className={`advisor-carousel-wrapper ${className}`}
			style={{
				width: '100%',
				maxWidth: '100%',
				position: 'relative',
				isolation: 'isolate',
				overflow: 'hidden',
			}}
		>
			<Slider
				className="advisor-carousel-slider"
				ref={sliderRef}
				infinite={showControls}
				speed={500}
				slidesToShow={1}
				slidesToScroll={1}
				autoplay={false}
				arrows={false}
				dots={showControls}
				adaptiveHeight={false}
				variableWidth={false}
				customPaging={(i) => (
					<div className="advisor-carousel-dot">
						<span className="advisor-carousel-dot-number">{i + 1}</span>
					</div>
				)}
				appendDots={(dots) => (
					<ul
						className="advisor-carousel-dots"
						style={{ margin: '0px', padding: '0px', listStyle: 'none' }}
					>
						{dots}
					</ul>
				)}
			>
				{groupedItems.map((slideItems, slideIndex) => (
					<div
						key={slideIndex}
						className={`advisor-carousel-slide ${slideClassName}`}
					>
						<div className="advisor-carousel-slide-content">
							<div
								className="dg advisor-carousel-grid"
								style={{
									width: '100%',
									gridTemplateColumns: `repeat(${columns}, 1fr)`,
									boxSizing: 'border-box',
									rowGap: '1rem',
									columnGap: '1rem',
									...gridStyle,
								}}
							>
								{slideItems.map((item, itemIndex) => (
									<div
										key={itemIndex}
										className="advisor-carousel-item"
										style={{
											width: '100%',
											minWidth: 0,
											boxSizing: 'border-box',
										}}
									>
										{item}
									</div>
								))}
							</div>
						</div>
					</div>
				))}
			</Slider>

			{showControls && (
				<>
					<button
						type="button"
						className="advisor-carousel-nav advisor-carousel-prev bg3 br100 df jcc aic cp"
						onClick={() => sliderRef.current?.slickPrev()}
						aria-label="Previous slide"
					>
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M12.5 15L7.5 10L12.5 5"
								stroke="white"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<button
						type="button"
						className="advisor-carousel-nav advisor-carousel-next bg3 br100 df jcc aic cp"
						onClick={() => sliderRef.current?.slickNext()}
						aria-label="Next slide"
					>
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M7.5 5L12.5 10L7.5 15"
								stroke="white"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
				</>
			)}
		</div>
	);
};

export default CommonSlider;
